/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useInactivityDetection } from './use-inactivity-detection'
import { useTabVisibility } from './use-tab-visibility'

export type StepName =
  | 'classification'
  | 'clustering'
  | 'data_extraction'
  | 'tagging'
  | 'patient_matching'
  | 'eob_payment_deposit'
  | 'eob_duplication'
  | 'eob_processing'

export interface StepSession {
  step: StepName
  documentKey: string | null
  stepStartTime: number
  hasDownloaded: boolean
  setHasDownloaded: () => void
  addIdleTime: (seconds: number) => void
  getIdleTime: () => number
}

const StepSessionContext = createContext<StepSession | null>(null)

interface StepSessionProviderProps {
  step: StepName
  documentKey: string | null
  children: ReactNode
}

export function StepSessionProvider({ step, documentKey, children }: StepSessionProviderProps) {
  const [hasDownloaded, setHasDownloadedState] = useState(false)
  const [stepStartTime, setStepStartTime] = useState(() => Date.now())
  const idleTimeRef = useRef(0)

  useEffect(() => {
    setHasDownloadedState(false)
    setStepStartTime(Date.now())
    idleTimeRef.current = 0
  }, [step, documentKey])

  const setHasDownloaded = useCallback(() => {
    setHasDownloadedState(true)
  }, [])

  const addIdleTime = useCallback((seconds: number) => {
    idleTimeRef.current += seconds
  }, [])

  const getIdleTime = useCallback(() => idleTimeRef.current, [])

  const value = useMemo<StepSession>(
    () => ({
      step,
      documentKey,
      stepStartTime,
      hasDownloaded,
      setHasDownloaded,
      addIdleTime,
      getIdleTime,
    }),
    [step, documentKey, stepStartTime, hasDownloaded, setHasDownloaded, addIdleTime, getIdleTime],
  )

  return (
    <StepSessionContext.Provider value={value}>
      <IdleDetectors />
      {children}
    </StepSessionContext.Provider>
  )
}

function IdleDetectors() {
  useTabVisibility()
  useInactivityDetection()
  return null
}

export function useStepSession(): StepSession {
  const context = useContext(StepSessionContext)
  if (!context) {
    throw new Error('useStepSession must be used within a StepSessionProvider')
  }
  return context
}
